import {
  FAVICON_DATA_URI_PATTERN,
  MAX_FAVICON_DATA_URI_LENGTH,
  PUBLIC_RESOURCE_FAVICON_FILENAME_PATTERN,
  PUBLIC_RESOURCE_IMAGE_FILENAME_PATTERN,
  PUBLIC_RESOURCE_URL_PATH_PATTERN,
  PUBLIC_RESOURCES_API_PATH,
  PUBLIC_RESOURCES_PATH,
  LOGIN_BACKGROUND_CSS_VAR,
  PUBLIC_RESOURCE_FILENAME_PATTERN,
} from '@/shared/constants/custom-styles';
import { normalizeApiBaseUrl, resolveApiOrigin } from '@/utils/api-url';
import { isAllowlistedExternalUrl } from '@/utils/external-domain-allowlist';

/** Bare CSS filenames only — custom_css must never resolve a .js resource. */
const CUSTOM_CSS_FILENAME_PATTERN = /^[\w.-]+\.css$/i;

/** Appends ?v=timestamp so API resource updates are picked up without a stale browser cache. */
export function withCacheBust(url: unknown, version = Date.now()): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${version}`;
}

/**
 * Resolves custom_css values: API public paths, .css filenames, API-origin URLs,
 * or https URLs on a hostname listed in app_external_domain_allowlist.
 */
export function resolveCustomCssUrl(apiBaseUrl: unknown, value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const trimmed = value.trim();
  const base = normalizeApiBaseUrl(apiBaseUrl);

  if (trimmed.startsWith(PUBLIC_RESOURCES_API_PATH) || trimmed.startsWith(PUBLIC_RESOURCES_PATH)) {
    if (!base) {
      return null;
    }

    return `${base}${trimmed.startsWith(PUBLIC_RESOURCES_API_PATH) ? trimmed.replace('/api/v2', '') : trimmed}`;
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (isAllowlistedExternalUrl(trimmed)) {
      return trimmed;
    }

    try {
      const url = new URL(trimmed);
      const apiOrigin = resolveApiOrigin(apiBaseUrl);

      if (apiOrigin && url.origin === apiOrigin && PUBLIC_RESOURCE_URL_PATH_PATTERN.test(url.pathname)) {
        return trimmed;
      }
    } catch {
      return null;
    }

    return null;
  }

  if (trimmed.startsWith('/')) {
    return null;
  }

  if (!CUSTOM_CSS_FILENAME_PATTERN.test(trimmed)) {
    return null;
  }

  return resolveCustomResourceUrl(apiBaseUrl, trimmed);
}

/** Resolves a custom resource: API filename, API public path, API-origin URL, or allowlisted https. */
export function resolveCustomResourceUrl(apiBaseUrl: unknown, file: unknown): string | null {
  if (typeof file !== 'string' || !file.trim()) {
    return null;
  }

  const trimmed = file.trim();

  if (trimmed.startsWith(PUBLIC_RESOURCES_API_PATH) || trimmed.startsWith(PUBLIC_RESOURCES_PATH)) {
    const base = normalizeApiBaseUrl(apiBaseUrl);

    if (!base) {
      return null;
    }

    return `${base}${trimmed.startsWith(PUBLIC_RESOURCES_API_PATH) ? trimmed.replace('/api/v2', '') : trimmed}`;
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (isAllowlistedExternalUrl(trimmed)) {
      return trimmed;
    }

    try {
      const url = new URL(trimmed);
      const apiOrigin = resolveApiOrigin(apiBaseUrl);

      if (apiOrigin && url.origin === apiOrigin && PUBLIC_RESOURCE_URL_PATH_PATTERN.test(url.pathname)) {
        return trimmed;
      }
    } catch {
      return null;
    }

    return null;
  }

  if (trimmed.startsWith('/')) {
    return null;
  }

  const base = normalizeApiBaseUrl(apiBaseUrl);

  if (!base || !PUBLIC_RESOURCE_FILENAME_PATTERN.test(trimmed)) {
    return null;
  }

  return `${base}${PUBLIC_RESOURCES_PATH}${trimmed}`;
}

const PUBLIC_RESOURCES_MARKER = '/public/resources/';

export function hasPublicResourcesSegment(path: string): boolean {
  return path.includes(PUBLIC_RESOURCES_MARKER) || /\/public\/resources$/.test(path);
}

function lastPathSegment(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

function isUnsafeRelativeResourcePath(path: string): boolean {
  return path.includes('..') || path.includes('\\') || path.includes('://') || path.startsWith('//');
}

/**
 * Keep the settings path as stored. Only prefix the API public-resources mount when
 * the value has no host and does not already contain `/public/resources`.
 */
export function prefixPublicResourcesIfMissing(
  apiBaseUrl: unknown,
  settingPath: string,
  filenamePattern: RegExp = PUBLIC_RESOURCE_IMAGE_FILENAME_PATTERN,
): string | null {
  const apiBase = normalizeApiBaseUrl(apiBaseUrl);

  if (!apiBase) {
    return null;
  }

  const path = (settingPath.startsWith('/') ? settingPath : `/${settingPath}`).split('?')[0].split('#')[0];

  if (isUnsafeRelativeResourcePath(path) || !filenamePattern.test(lastPathSegment(path))) {
    return null;
  }

  if (hasPublicResourcesSegment(path)) {
    if (path.startsWith('/public/resources/')) {
      return `${apiBase}${path}`;
    }

    return path;
  }

  return `${apiBase}${PUBLIC_RESOURCES_PATH}${path.replace(/^\//, '')}`;
}

function sniffImageMimeFromBase64(payload: string): string | null {
  let bytes: Uint8Array;

  try {
    const binary = atob(payload);
    bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    return null;
  }

  if (bytes.length < 12) {
    return null;
  }

  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }

  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }

  if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01 && bytes[3] === 0x00) {
    return 'image/x-icon';
  }

  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }

  return null;
}

function resolveFaviconDataUri(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const compact = value.trim().replace(/\s+/g, '');

  if (compact.length > MAX_FAVICON_DATA_URI_LENGTH) {
    return null;
  }

  if (FAVICON_DATA_URI_PATTERN.test(compact)) {
    return compact;
  }

  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(compact)) {
    return null;
  }

  const mime = sniffImageMimeFromBase64(compact);

  if (!mime) {
    return null;
  }

  const dataUri = `data:${mime};base64,${compact}`;

  if (dataUri.length > MAX_FAVICON_DATA_URI_LENGTH) {
    return null;
  }

  return dataUri;
}

/** Favicon setting: image data URI / raw image base64, or the same path forms as logos. */
export function resolveCustomFaviconUrl(apiBaseUrl: unknown, value: unknown): string | null {
  const dataUri = resolveFaviconDataUri(value);

  if (dataUri) {
    return dataUri;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const trimmed = value.trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return resolveCustomResourceUrl(apiBaseUrl, trimmed);
  }

  return prefixPublicResourcesIfMissing(apiBaseUrl, trimmed, PUBLIC_RESOURCE_FAVICON_FILENAME_PATTERN);
}

const injectedLinks = new Map<string, HTMLLinkElement>();
let injectedFaviconUrl: string | null = null;
let originalFaviconHref: string | null = null;
let originalFaviconType: string | null = null;
let injectedLoginBackgroundUrl: string | null = null;

function readLinkHref(link: HTMLLinkElement): string {
  if (typeof link.getAttribute === 'function') {
    return link.getAttribute('href') ?? '';
  }

  return link.href ?? '';
}

function readLinkType(link: HTMLLinkElement): string {
  if (typeof link.getAttribute === 'function') {
    return link.getAttribute('type') ?? '';
  }

  return link.type ?? '';
}

function rememberOriginalFavicon(link: HTMLLinkElement) {
  if (originalFaviconHref != null) {
    return;
  }

  originalFaviconHref = readLinkHref(link);
  originalFaviconType = readLinkType(link);
}

function faviconTypeFromUrl(url: string): string {
  if (url.startsWith('data:')) {
    return url.match(/^data:([^;,]+)/i)?.[1] ?? '';
  }

  const path = url.split('?')[0].split('#')[0].toLowerCase();

  if (path.endsWith('.svg')) {
    return 'image/svg+xml';
  }

  if (path.endsWith('.png')) {
    return 'image/png';
  }

  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) {
    return 'image/jpeg';
  }

  if (path.endsWith('.gif')) {
    return 'image/gif';
  }

  if (path.endsWith('.webp')) {
    return 'image/webp';
  }

  if (path.endsWith('.ico')) {
    return 'image/x-icon';
  }

  return '';
}

function applyFaviconType(link: HTMLLinkElement, url: string) {
  const nextType = url === originalFaviconHref ? (originalFaviconType ?? '') : faviconTypeFromUrl(url);
  link.type = nextType;

  if (!nextType && typeof link.removeAttribute === 'function') {
    link.removeAttribute('type');
  }
}

export function removeInjectedLink(key: string, _options: { head?: ParentNode | null } = {}) {
  const element = injectedLinks.get(key);

  if (element?.parentNode) {
    element.parentNode.removeChild(element);
  }

  injectedLinks.delete(key);
}

export function injectStylesheetLink(
  key: string,
  url: string | null,
  {
    head = typeof document !== 'undefined' ? document.head : null,
    dataset = {},
    bustCache = false,
    cacheVersion = Date.now(),
  }: {
    head?: ParentNode | null;
    dataset?: Record<string, string>;
    bustCache?: boolean;
    cacheVersion?: number;
  } = {},
) {
  const href = bustCache ? withCacheBust(url, cacheVersion) : url;

  if (!href) {
    removeInjectedLink(key, { head });
    return;
  }

  if (!head) {
    return;
  }

  const existing = injectedLinks.get(key);

  if (existing?.href === href) {
    return;
  }

  removeInjectedLink(key, { head });

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.customStyleKey = key;

  for (const [name, value] of Object.entries(dataset)) {
    link.dataset[name] = value;
  }

  head.appendChild(link);
  injectedLinks.set(key, link);
}

export function applyFaviconUrl(
  url: string | null,
  { head = typeof document !== 'undefined' ? document.head : null }: { head?: ParentNode | null } = {},
) {
  if (!head) {
    return;
  }

  let link = head.querySelector('link[rel="icon"]') as HTMLLinkElement | null;

  if (!link) {
    if (!url) {
      return;
    }

    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }

  rememberOriginalFavicon(link);

  const nextUrl = url || originalFaviconHref;

  if (!nextUrl || injectedFaviconUrl === nextUrl) {
    return;
  }

  link.href = nextUrl;
  applyFaviconType(link, nextUrl);
  injectedFaviconUrl = nextUrl;
}

export function applyLoginBackgroundUrl(
  url: string | null,
  { root = typeof document !== 'undefined' ? document.documentElement : null }: { root?: HTMLElement | null } = {},
) {
  if (!root) {
    return;
  }

  if (!url) {
    root.style.removeProperty(LOGIN_BACKGROUND_CSS_VAR);
    injectedLoginBackgroundUrl = null;
    return;
  }

  if (injectedLoginBackgroundUrl === url) {
    return;
  }

  root.style.setProperty(LOGIN_BACKGROUND_CSS_VAR, `url("${url}")`);
  injectedLoginBackgroundUrl = url;
}

export function resetCustomStyleInjectionState() {
  injectedLinks.clear();
  injectedFaviconUrl = null;
  originalFaviconHref = null;
  originalFaviconType = null;
  injectedLoginBackgroundUrl = null;
}
